// File: src/components/ProductCategoryCard.jsx
// Single product discovery card with 2x2 grid layout
// Amazon-style category discovery pattern
// Routes: Subcategory items → /subcategory/:slug | Footer → /category/:slug

import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const ProductCategoryCard = ({ category }) => {
  const [hoveredItem, setHoveredItem] = useState(null);

  // Validate category has required data (exactly 4 subcategories)
  if (!category || !category.items || category.items.length < 4) {
    return null;
  }

  return (
    <div className="bg-white rounded-md shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden border border-gray-100 flex flex-col h-full">
      
      {/* Card Header - Category Title */}
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
        <h3 className="text-sm font-semibold text-gray-900 line-clamp-1">
          {category.title}
        </h3>
      </div>

      {/* 2x2 Subcategory Grid - Amazon Discovery Pattern */}
      <div className="flex-grow p-4">
        <div className="grid grid-cols-2 gap-3">
          {category.items.slice(0, 4).map((item) => (
            <Link
              key={item.id}
              to={item.link}
              className="flex flex-col group"
              onMouseEnter={() => setHoveredItem(item.id)}
              onMouseLeave={() => setHoveredItem(null)}
            >
              {/* Image Container - 1:1 Aspect Ratio (prevents layout shift) */}
              <div 
                className="relative overflow-hidden bg-gray-50 rounded mb-2 transition-all duration-300"
                style={{ aspectRatio: '1 / 1' }}
              >
                <img
                  src={item.image}
                  alt={item.label}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                  decoding="async"
                  onError={(e) => {
                    e.target.src = '/api/placeholder/300/300';
                    e.target.alt = 'Category image unavailable';
                  }}
                />
                
                {/* Subtle hover overlay - minimal interaction feedback */}
                {hoveredItem === item.id && (
                  <div className="absolute inset-0 bg-black/5 transition-all duration-300" />
                )}
              </div>

              {/* Subcategory Label - Clear name display */}
              <p className="text-xs font-medium text-gray-700 text-center line-clamp-2 group-hover:text-gray-900 transition-colors duration-200">
                {item.label}
              </p>
            </Link>
          ))}
        </div>
      </div>

      {/* Footer CTA - Navigate to full category page */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
        <Link
          to={category.categoryLink}
          className="text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors duration-200 inline-flex items-center gap-1"
        >
          See all
          <span className="text-sm">›</span>
        </Link>
      </div>
    </div>
  );
};

export default ProductCategoryCard;
